/**
 * Formatting utilities for dates, times, frequencies, and other display values
 */

import { sanitizePlainText } from 't-lib-ui/connector';

export { formatPublishedTime, parseAuthor } from 't-lib-postviewer';

/**
 * Formats a publication date into a human-readable relative time string
 * Used for displaying post publication dates (e.g., "5m ago", "3h ago", "Jan 15, 2026")
 */
export function formatPostDate(pubDate?: string): string {
  if (!pubDate) return '';

  const postTime = new Date(pubDate).getTime();

  // Check if the date is invalid (NaN)
  if (isNaN(postTime)) {
    // Return the original string, sanitized and truncated
    const sanitized = sanitizePlainText(pubDate);
    return sanitized.length > 24 ? sanitized.slice(0, 24) : sanitized;
  }

  const now = Date.now();
  const diff = now - postTime;

  if (diff < 0) return 'In the future';

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) {
    return `${minutes}m ago`;
  } else if (hours < 24) {
    return `${hours}h ago`;
  } else if (days < 7) {
    return `${days}d ago`;
  } else {
    const postDate = new Date(pubDate);
    return postDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

/**
 * Formats a publication date into a detailed relative time string
 * Used for displaying last post times in feed tiles (e.g., "5 minutes ago", "today at 3:45 PM")
 */
export function formatLastPostTime(pubDate?: string): string {
  if (!pubDate) return '';

  const postTime = new Date(pubDate).getTime();

  // Check if the date is invalid (NaN)
  if (isNaN(postTime)) {
    // Return the original string, sanitized and truncated
    const sanitized = sanitizePlainText(pubDate);
    return sanitized.length > 24 ? sanitized.slice(0, 24) : sanitized;
  }

  const now = Date.now();
  const diff = now - postTime;

  if (diff < 0) return 'In the future';

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  const postDate = new Date(pubDate);

  if (minutes < 60) {
    return `${minutes} minute${minutes != 1 ? 's' : ''} ago`;
  } else if (hours < 24) {
    const time = postDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `today at ${time}`;
  } else if (days == 1) {
    const time = postDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `yesterday at ${time}`;
  } else if (days < 7) {
    return `${days} days ago`;
  } else {
    return postDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

/** "one hour", "two days", "5 weeks": small counts spelled out, units pluralized. */
const countUnit = (n: number, unit: string) => `${n == 1 ? 'one' : n == 2 ? 'two' : n} ${unit}${n == 1 ? '' : 's'}`;

/**
 * Coarse age of a post for badges, e.g. "minutes ago", "3 hours ago", "today", "yesterday",
 * "last week", "two months ago". Days are counted in calendar days of the local time zone.
 * Returns an empty string for missing or unparseable dates.
 */
export function formatPostAge(pubDate?: string, now = new Date()): string {
  if (!pubDate) return '';
  const postDate = new Date(pubDate);
  const diff = now.getTime() - postDate.getTime();
  if (isNaN(diff)) return '';

  const hours = Math.floor(diff / 3600000);
  // Future dates (clock skew, bad feeds) count as brand new.
  if (hours < 1) return 'minutes ago';
  if (hours < 6) return `${countUnit(hours, 'hour')} ago`;

  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  // Rounded, since DST days are not 24h long.
  const calendarDays = Math.round((startOfDay(now) - startOfDay(postDate)) / 86400000);
  if (!calendarDays) return 'today';
  if (calendarDays == 1) return 'yesterday';
  if (calendarDays < 7) return `${countUnit(calendarDays, 'day')} ago`;

  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(days / 7);
  if (weeks < 2) return 'last week';
  if (weeks < 5) return `${countUnit(weeks, 'week')} ago`;

  const months = Math.floor(days / 30.44);
  if (months < 12) return `${countUnit(months, 'month')} ago`;
  return `${countUnit(Math.floor(days / 365.25), 'year')} ago`;
}

/**
 * Rounds a number to its first significant digit
 * Used for displaying approximate frequencies (e.g., 123 -> 100, 7 -> 7)
 */
function roundToFirstDigit(num: number): number {
  if (num < 10) return num;
  const magnitude = Math.pow(10, Math.floor(Math.log10(num)));
  return Math.round(num / magnitude) * magnitude;
}

/**
 * Formats posting frequency (posts per day) into a human-readable string
 * (e.g., "about 5 posts/day", "about 2 posts/week", "very rare posts")
 */
export function formatFrequency(freq?: number): string {
  if (!freq || freq <= 0) return '';

  if (freq >= 1) {
    // At least 1 post per day
    const perDay = roundToFirstDigit(Math.round(freq));
    return `about ${perDay} post${perDay != 1 ? 's' : ''}/day`;
  } else if (freq >= 0.8 / 7) {
    // At least 0.8 posts per week (i.e., >= 0.114 posts/day)
    const perWeek = roundToFirstDigit(Math.round(freq * 7));
    return `about ${perWeek} post${perWeek != 1 ? 's' : ''}/week`;
  } else if (freq >= 0.9 / 30) {
    // At least 0.9 posts per month (i.e., >= 0.03 posts/day)
    const perMonth = roundToFirstDigit(Math.round(freq * 30));
    return `about ${perMonth} post${perMonth != 1 ? 's' : ''}/month`;
  } else if (freq >= 1 / 365) {
    // At least 1 post per year
    const perYear = roundToFirstDigit(Math.round(freq * 365));
    return `about ${perYear} post${perYear != 1 ? 's' : ''}/year`;
  } else {
    return 'very rare posts';
  }
}

/**
 * Stable "semi-random" accent color for a name (folder icons, fallback feed tiles): the name is
 * hashed to a hue; fixed saturation/lightness keep it readable in both themes.
 */
export function nameColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 62%, 43%)`;
}

/**
 * Compact posting frequency (posts per day) for small tiles: "10 per day", "4 per week",
 * "2 per month", "3 per year". Empty when unknown or rarer than once a year.
 */
export function formatShortFrequency(freq?: number): string {
  if (!freq || freq < 1 / 365) return '';
  if (freq >= 1) return `${roundToFirstDigit(Math.round(freq))} per day`;
  if (freq >= 0.8 / 7) return `${roundToFirstDigit(Math.round(freq * 7))} per week`;
  if (freq >= 0.9 / 30) return `${roundToFirstDigit(Math.round(freq * 30))} per month`;
  return `${roundToFirstDigit(Math.round(freq * 365))} per year`;
}

/**
 * One-word-ish label for a feed from its post-stats badges (see schema-copy/post-stats-badges):
 * media wins ("Video", "Audio"), otherwise the text badge ("Full posts", "Summaries", ...).
 */
export function formatFeedType(types: string[]): string {
  const type = types.find(t => t == 'videos' || t == 'audio') ?? types[0];
  if (!type) return '';
  return type == 'videos' ? 'Video' : type[0].toUpperCase() + type.slice(1);
}

/**
 * Formats feed category codes into display names
 * Returns null for categories that should be hidden (adult, piracy+illegal)
 */
export function formatCategory(category?: string): string | null {
  if (!category) return null;

  // Exclude these categories from display
  if (category == 'adult' || category == 'piracy+illegal') {
    return null;
  }

  // Override table for categories that don't follow the default capitalization pattern
  const categoryOverrides: Record<string, string> = {
    'tech': 'Technology',
    'tech/ai': 'Technology/AI',
    'tech/audio': 'Technology/Audio',
    'tech/video': 'Technology/Video',
    'tech/mobile': 'Technology/Mobile',
    'tech/tinkering': 'Technology/Tinkering',
    'tech/vintage': 'Technology/Vintage',
    'tech/crypto': 'Technology/Crypto',
    'tech/robotics': 'Technology/Robotics',
    'diy': 'DIY',
    'it': 'IT',
    'it/security': 'IT/Security',
    'lifenhealth': 'Life',
    'film+tv': 'Film & TV',
    'software-development/ai': 'Software Dev/AI',
    'software-development/javascript': 'Software Dev/JavaScript',
    'software-development/devops': 'Software Dev/DevOps',
    'software-development/linux': 'Software Dev/Linux',
    'software-development/microsoft': 'Software Dev/Microsoft',
    'business/industry-news': 'Business/Industry News',
    'music/rnb': 'Music/R&B',
    'music/hip-hop': 'Music/Hip-Hop',
    'gaming/pc': 'Gaming/PC',
    'gaming/playstation': 'Gaming/PlayStation',
    'travel/theme-parks': 'Travel/Theme Parks',
    'marketing/seo': 'Marketing/SEO',
    'space': 'Space',
    'news/us': 'US News',
    'news/uk': 'UK News',
    'news/aunz': 'Australia / NZ News',
    'news/ca': 'Canada News',
    'news/international': 'International News',
    'news/local': 'Local News',
    'defense': 'Defense & Military',
    'piracy+illegal': 'Piracy & Illegal'
  };

  if (categoryOverrides[category]) {
    return categoryOverrides[category];
  }

  // Default: capitalize first letter and first letter after '/', '-', or '+'
  return category.replace('-', ' ').replace('software development', 'Software Dev')
    .split(/([/\-+])/)
    .map(part => {
      if (part == '/' || part == '-' || part == '+' || part == ' ') return part == '+' ? ' & ' : part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join('');
}

/**
 * Formats fetch interval in minutes to a human-readable string
 * (e.g., "30 minutes", "2 hours", "7 days")
 */
export function formatFetchInterval(intervalMins?: number): string {
  if (!intervalMins) return 'Unknown';
  if (intervalMins < 60) return `${intervalMins} minute${intervalMins != 1 ? 's' : ''}`;
  const hours = Math.round(intervalMins / 60);
  if (intervalMins < 1440) return `${hours} hour${hours != 1 ? 's' : ''}`;
  const days = Math.round(intervalMins / 1440);
  return `${days} day${days != 1 ? 's' : ''}`;
}

/**
 * Formats the time since a feed's last fetch for the stale-preview warning
 * (e.g., "Last fetch: 5 hours ago", "Last fetch: 3 days ago" when >= 48h).
 * Returns undefined when the feed was fetched within the last 2 hours (no warning needed)
 * or the timestamp is missing/invalid.
 */
export function formatLastFetch(lastFetchedAt?: string): string | undefined {
  if (!lastFetchedAt) return undefined;
  const fetchTime = new Date(lastFetchedAt).getTime();
  if (isNaN(fetchTime)) return undefined;
  const hours = Math.floor((Date.now() - fetchTime) / 3600000);
  if (hours < 2) return undefined;
  if (hours >= 48) {
    const days = Math.floor(hours / 24);
    return `Last fetch: ${days} days ago`;
  }
  return `Last fetch: ${hours} hours ago`;
}

/**
 * Formats a post count into a human-readable string
 * - Less than 20: exact number (e.g., "15 posts")
 * - 20-199: rounded down to 10 with '+' (e.g., "160+ posts")
 * - 200-999: rounded down to 100 with '+' (e.g., "300+ posts")
 * - 1000+: "999+ posts"
 */
export function formatPostCount(count: number): string {
  if (count < 20) return `${count} post${count != 1 ? 's' : ''}`;
  if (count < 200) {
    const rounded = Math.floor(count / 10) * 10;
    return `${rounded}+ posts`;
  }
  if (count < 1000) {
    const rounded = Math.floor(count / 100) * 100;
    return `${rounded}+ posts`;
  }
  return '999+ posts';
}

/**
 * Formats an unread count for display in the sidebar
 * Caps at 999+ to avoid overflow
 */
export function formatUnreadCount(count: number): string {
  return count > 999 ? '999+' : count.toString();
}

/**
 * Formats a large global stat count, rounded to `roundTo` for a "fuzzy" feel.
 * Examples (roundTo 100): 3540 -> "3.5k", 10500 -> "10.5k", 105300 -> "105.3k".
 * Examples (roundTo 1000): 244100 -> "244k", 2333000 -> "2.3 million".
 * With `short`, millions abbreviate to "2.3M" so the value fits narrow layouts.
 */
export function formatStatCount(n: number, roundTo: number, short = false): string {
  const r = Math.round(n / roundTo) * roundTo;
  const trim = (x: number) => (x % 1 == 0 ? String(x) : x.toFixed(1));
  if (r >= 1000000) return `${trim(r / 1000000)}${short ? 'M' : ' million'}`;
  if (r >= 1000) return `${trim(r / 1000)}k`;
  return String(r);
}

/**
 * Formats a date for list mode: time if <24h old, short date otherwise.
 * Same day: "3:29 p.m.", This year: "Jan 2", Previous year: "Jan 2, 2024"
 */
export const formatListTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < 86400000 && diffMs >= 0) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  if (date.getFullYear() == now.getFullYear()) return `${month} ${day}`;
  return `${month} ${day}, ${date.getFullYear()}`;
};

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
  nbsp: '\u00A0', ensp: '\u2002', emsp: '\u2003',
  mdash: '\u2014', ndash: '\u2013',
  laquo: '\u00AB', raquo: '\u00BB', hellip: '\u2026',
  ldquo: '\u201C', rdquo: '\u201D', lsquo: '\u2018', rsquo: '\u2019',
  copy: '\u00A9', reg: '\u00AE', trade: '\u2122',
};

let entityDecoder: HTMLTextAreaElement | undefined;

function decodeNamedEntity(entity: string): string {
  if (!entityDecoder) entityDecoder = document.createElement('textarea');
  entityDecoder.innerHTML = `&${entity};`;
  return entityDecoder.value;
}

/**
 * Decodes HTML entities in a string, leaving other content unchanged.
 */
export function decodeEntities(text: string | undefined): string {
  if (!text) return '';
  return text.replace(/&(?:([a-zA-Z]+)|#x([0-9a-fA-F]+)|#(\d+));/g, (_match, name, hex, dec) => {
    if (name) return NAMED_ENTITIES[name] ?? decodeNamedEntity(name);
    if (hex) return String.fromCodePoint(parseInt(hex, 16));
    return String.fromCodePoint(parseInt(dec, 10));
  });
}

// <aside> content stands beside the article — a related-links box, an editor's
// note, a subscribe prompt — and publishers often put it first. Stripped down to
// bare text it reads as the article's opening sentence, so previews drop it.
const ASIDE_BLOCK = /<aside\b[^>]*>[\s\S]*?<\/aside>/gi;

/**
 * Strips HTML tags and decodes HTML entities, returning plain text truncated to maxLength.
 * <aside> blocks are dropped, unless the post is nothing but an aside (some CMSes wrap
 * the whole entry in one) — an empty preview would be worse than a sidebar one.
 */
export function stripHtml(html: string | undefined, maxLength = 200): string {
  if (!html) return '';
  const toText = (h: string) => decodeEntities(h.replace(/<[^>]*>/g, '')).trim();
  const stripped = toText(html.replace(ASIDE_BLOCK, ' ')) || toText(html);
  return stripped.length > maxLength ? stripped.substring(0, maxLength) + '...' : stripped;
}

/**
 * Formats a date into a compact relative time string for admin lists
 * (e.g., "5m ago", "3h ago", "in 50m", "yesterday", "3d ago", "2w ago", "3mo ago", "1y ago")
 * Relative to local timezone, not UTC
 */
export function formatCompactDate(dateStr?: string): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  // Less than 64 minutes: show in minutes (past or future)
  if (Math.abs(diffMinutes) < 64) {
    if (diffMinutes >= 0) {
      return `${diffMinutes}m ago`;
    } else {
      return `in ${-diffMinutes}m`;
    }
  }

  // Less than 26 hours: show in hours (past or future)
  if (Math.abs(diffHours) < 26) {
    if (diffHours >= 0) {
      return `${diffHours}h ago`;
    } else {
      return `in ${-diffHours}h`;
    }
  }

  // Get the local date at midnight for today
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Get the local date at midnight for the input date
  const inputMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Calculate difference in days based on local dates
  const diffDays = Math.floor((todayMidnight.getTime() - inputMidnight.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays == 0) return 'today';
  if (diffDays == 1) return 'yesterday';
  if (diffDays == -1) return 'tomorrow';
  if (diffDays < 0) return `in ${-diffDays}d`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

